import { useState, useEffect, useCallback } from 'react';
import { api, SessionExpiredError } from '../lib/api';
import { getInitData } from '../lib/telegram';
import { storeLandingWuid, consumeLandingWuid, reportRegistration } from '../lib/landingReferral';
import { flushAppEvents } from '../lib/appEvents';
import { storeCalculatorHandoff } from '../lib/calculatorHandoff';
import { getStoredLocale, setLocale } from '../i18n';
import type { AuthResponse, TelegramLoginData } from '@etap/shared';

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthResponse['user'] | null;
  error: string | null;
  needsTelegramOAuth: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    error: null,
    needsTelegramOAuth: false,
  });

  const authenticate = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null, needsTelegramOAuth: false }));

    // Сохраняем wuid из URL лендинга (если есть)
    storeLandingWuid();
    // И результат калькулятора — до авторизации, иначе OAuth-редирект его съест
    storeCalculatorHandoff();

    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Проверяем флаг намеренного логаута
      const intentionalLogout = localStorage.getItem('etap_intentional_logout') === 'true';

      // Проверяем, есть ли сохраненный токен
      const existingToken = api.getToken();
      if (existingToken) {
        try {
          // Получаем данные пользователя по токену
          const result = await api.getMe();
          if (result.user.locale) setLocale(result.user.locale);
          // Очищаем флаг логаута при успешной авторизации
          localStorage.removeItem('etap_intentional_logout');
          setState({
            isLoading: false,
            isAuthenticated: true,
            user: result.user,
            error: null,
            needsTelegramOAuth: false,
          });
          return;
        } catch (err) {
          // Сессию отвергли — request() уже очистил токен, продолжаем авторизацию.
          // Сбой сети/сервера на старте — не разлогин: токен остаётся до следующей попытки.
          // В Telegram всё равно можно перелогиниться по initData.
          if (!(err instanceof SessionExpiredError) && !getInitData()) throw err;
        }
      }

      // Получаем initData из Telegram
      const initData = getInitData();

      if (initData) {
        // Авторизуемся через Telegram
        const wuid = consumeLandingWuid();
        const localLocale = getStoredLocale();
        const result = await api.authenticateWithTelegram(initData, timeZone, wuid ?? undefined, localLocale);
        // Только после успеха: до этого WUID должен пережить возможную ошибку.
        reportRegistration(result.isNewUser);
        // Токен появился — досылаем всё, что накопилось до авторизации.
        flushAppEvents();
        if (result.user.locale) setLocale(result.user.locale);
        // Очищаем флаг логаута при успешной авторизации
        localStorage.removeItem('etap_intentional_logout');
        setState({
          isLoading: false,
          isAuthenticated: true,
          user: result.user,
          error: null,
          needsTelegramOAuth: false,
        });
        return;
      }

      // В dev режиме автоматически авторизуемся через dev endpoint
      // НО только если пользователь не вышел намеренно
      if (import.meta.env.DEV && !intentionalLogout) {
        console.log('🔧 Dev mode: authenticating via /auth/dev');
        const result = await api.authenticateDev(timeZone, getStoredLocale());
        if (result.user.locale) setLocale(result.user.locale);
        setState({
          isLoading: false,
          isAuthenticated: true,
          user: result.user,
          error: null,
          needsTelegramOAuth: false,
        });
        return;
      }

      setState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: null,
        needsTelegramOAuth: true,
      });
    } catch (err) {
      console.error('Auth error:', err);
      setState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: err instanceof Error ? err.message : 'Authentication failed',
        needsTelegramOAuth: !getInitData(),
      });
    }
  }, []);

  const authenticateWithOAuth = useCallback(async (authData: TelegramLoginData) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const wuid = consumeLandingWuid();
      const result = await api.authenticateWithTelegramLogin(authData, timeZone, wuid ?? undefined, getStoredLocale());
      reportRegistration(result.isNewUser);
        // Токен появился — досылаем всё, что накопилось до авторизации.
        flushAppEvents();
      if (result.user.locale) setLocale(result.user.locale);
      // Очищаем флаг логаута при успешной авторизации
      localStorage.removeItem('etap_intentional_logout');
      setState({
        isLoading: false,
        isAuthenticated: true,
        user: result.user,
        error: null,
        needsTelegramOAuth: false,
      });
    } catch (err) {
      console.error('OAuth auth error:', err);
      setState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: err instanceof Error ? err.message : 'Не удалось авторизоваться через Telegram',
        needsTelegramOAuth: true,
      });
    }
  }, []);

  useEffect(() => {
    authenticate();
  }, [authenticate]);

  const logout = useCallback(async () => {
    // Вызываем API logout для revoke refresh token
    await api.logout();

    // Устанавливаем флаг намеренного логаута
    localStorage.setItem('etap_intentional_logout', 'true');
    setState({
      isLoading: false,
      isAuthenticated: false,
      user: null,
      error: null,
      needsTelegramOAuth: !getInitData(),
    });
  }, []);

  const logoutAll = useCallback(async () => {
    // Выход со всех устройств - отзывает все refresh tokens
    await api.logoutAll();

    localStorage.setItem('etap_intentional_logout', 'true');
    setState({
      isLoading: false,
      isAuthenticated: false,
      user: null,
      error: null,
      needsTelegramOAuth: !getInitData(),
    });
  }, []);

  return {
    ...state,
    authenticate,
    authenticateWithOAuth,
    logout,
    logoutAll,
  };
}
